import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Eye, EyeOff, Languages, Shield } from '@/lib/icons';
import { BrandLockup } from '@/components/branding/BrandLockup';
import { useBranding } from '@/components/branding/BrandingProvider';
import { LoginProductPanel } from '@/features/auth/login-product-panel';
import {
  allowsSignIn,
  loginPageState,
  namesTenant,
} from '@/features/auth/login-page-state';
import { Skeleton } from '@/components/ui/skeleton';

import { LOCAL_STORAGE_KEYS } from '@/constants';
import { useAppDispatch } from '@/store/hooks';
import { useToast } from '@/hooks/use-toast';
import { tokenRefreshService } from '@/lib/auth/token-refresh';
import type { TokenData } from '@/lib/auth/types';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { ButtonSpinner } from '@/components/feedback';
import { getCurrentUser, login } from '@/features/auth/api';
import { setUser } from '@/features/auth/auth-slice';
import { getLandingPath } from '@/lib/auth/route-access';

// Login validation schema factory
const createLoginSchema = (t: (key: string) => string) =>
  z.object({
    identifier: z
      .string()
      .min(5, t('auth.validation.identifierMinLength'))
      .refine(
        (val) => {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          const phoneRegex = /^[0-9+\s-]+$/;
          return emailRegex.test(val) || phoneRegex.test(val);
        },
        { message: t('auth.validation.identifierInvalid') }
      ),
    password: z.string().min(6, t('auth.validation.passwordMinLength')),
  });

type LoginFormValues = z.infer<ReturnType<typeof createLoginSchema>>;

export const Route = createFileRoute('/(auth)/login')({
  component: LoginComponent,
});

export function LoginComponent() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { resolution, isLoading } = useBranding();
  const [showPassword, setShowPassword] = useState(false);

  const pageState = loginPageState(resolution, isLoading);
  const showForm = allowsSignIn(pageState);

  const loginSchema = createLoginSchema(t);

  const changeLanguage = async (lng: string) => {
    await i18n.changeLanguage(lng);
    localStorage.setItem('i18nextLng', lng);
  };

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      identifier: '',
      password: '',
    },
  });

  const onSubmit = async (values: LoginFormValues) => {
    const result = await login(values.identifier, values.password);

    if (!result.succeeded) {
      toast({
        title: t('auth.login.toast.errorTitle'),
        description:
          result.errors?.[0] ?? t('auth.login.toast.errorDescription'),
        variant: 'destructive',
      });
      return;
    }

    let landingPath = '/';

    if (result.value) {
      const { accessToken, expiresIn, user } = result.value;

      // The refresh token is delivered as an HttpOnly cookie (T10/T11) and is
      // not present in the response body. We store only the access token and
      // its TTL; the token-refresh interceptor (T12) handles silent renewal.
      if (accessToken) {
        const now = Math.floor(Date.now() / 1000);
        const accessTtl = expiresIn ?? 3600;
        // Mirror the server's REFRESH_TOKEN_TTL_DAYS=14 default and token-refresh.ts.
        // Keeping these in sync prevents the visibilitychange handler from
        // force-logging-out a user whose HttpOnly cookie is still valid server-side.
        const refreshTtl = 14 * 24 * 60 * 60; // 14 days = 1209600 seconds

        const tokenData: TokenData = {
          access_token: accessToken,
          refresh_token: '',
          expiresIn: accessTtl,
          expiresAt: now + accessTtl,
          refreshTokenExpiresIn: refreshTtl,
          refreshTokenExpiresAt: now + refreshTtl,
        };

        tokenRefreshService.setToken(tokenData);
      }

      dispatch(setUser(user));
      localStorage.setItem(LOCAL_STORAGE_KEYS.USER, JSON.stringify(user));

      const freshResult = await getCurrentUser().catch(() => null);
      const fresh =
        freshResult?.succeeded && freshResult.value ? freshResult.value : user;
      if (fresh !== user) {
        dispatch(setUser(fresh));
      }
      localStorage.setItem(LOCAL_STORAGE_KEYS.USER, JSON.stringify(fresh));
      landingPath = getLandingPath(fresh);
    }

    toast({
      title: t('auth.login.toast.successTitle'),
      description: t('auth.login.toast.successDescription'),
    });

    navigate({ to: landingPath });
  };

  return (
    <div className="min-h-screen flex bg-background relative overflow-hidden">
      {/* Animated background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[40%] -right-[20%] w-[60%] h-[80%] rounded-full bg-gradient-to-br from-primary/8 to-primary/3 blur-3xl animate-pulse" />
        <div className="absolute -bottom-[30%] -left-[15%] w-[50%] h-[70%] rounded-full bg-gradient-to-tr from-primary/6 to-transparent blur-3xl" />
        <div className="absolute top-[20%] left-[30%] w-[30%] h-[30%] rounded-full bg-gradient-to-br from-accent/5 to-transparent blur-2xl" />
      </div>

      <LoginProductPanel state={pageState} />

      {/* Right panel — Login form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 lg:p-12 relative z-10">
        <div className="w-full max-w-[420px] space-y-8">
          {/* Mobile brand (shown only on small screens). */}
          {namesTenant(pageState) && (
            <div className="lg:hidden text-center space-y-3 flex flex-col items-center">
              <BrandLockup size="md" />
              <h1 className="text-2xl font-bold">
                <span className="text-primary">
                  {t('auth.login.mobileHeading')}
                </span>
              </h1>
            </div>
          )}

          {/* Login card */}
          <div className="bg-[hsl(var(--surface-container-lowest)/0.85)] backdrop-blur-xl rounded-2xl border border-[hsl(var(--foreground)/0.06)] shadow-[0_24px_40px_-8px_hsl(213_20%_15%/0.08)] p-8">
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  {showForm && (
                    <>
                      <h2 className="text-xl font-bold tracking-tight">
                        {t('auth.login.title')}
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {t('auth.login.description')}
                      </p>
                    </>
                  )}
                  {pageState.kind === 'loading' && (
                    <>
                      <Skeleton className="h-6 w-32" />
                      <Skeleton className="h-4 w-56" />
                    </>
                  )}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="rounded-xl h-9 w-9 hover:bg-muted"
                      type="button"
                    >
                      <Languages className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[140px]">
                    <DropdownMenuItem onClick={() => changeLanguage('en')}>
                      <span className="mr-2">🇺🇸</span>
                      <span>English</span>
                      {i18n.language === 'en' && (
                        <span className="ml-auto text-xs text-primary">✓</span>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => changeLanguage('so')}>
                      <span className="mr-2">🇸🇴</span>
                      <span>Soomaali</span>
                      {i18n.language === 'so' && (
                        <span className="ml-auto text-xs text-primary">✓</span>
                      )}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {pageState.kind === 'loading' && (
                <div className="space-y-5">
                  <Skeleton className="h-[68px] w-full rounded-xl" />
                  <Skeleton className="h-[68px] w-full rounded-xl" />
                  <Skeleton className="h-11 w-full rounded-xl" />
                </div>
              )}

              {/* Both are misconfigurations, not logins — but only one of them
                  means we failed to identify the host. */}
              {pageState.kind === 'unmapped' && (
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t('auth.login.tenantNotMapped')}
                </p>
              )}
              {pageState.kind === 'noProduct' && (
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t('auth.login.tenantNoProduct')}
                </p>
              )}

              {/* A resolve outage withholds the marketing, never the sign-in. */}
              {pageState.kind === 'unavailable' && (
                <div className="flex items-start gap-3 rounded-xl border border-[hsl(var(--foreground)/0.06)] bg-muted/50 p-3">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {t('auth.login.serviceUnavailable')}
                  </p>
                </div>
              )}

              {/* Form */}
              {showForm && (
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-5"
                  >
                    <FormField
                      control={form.control}
                      name="identifier"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium">
                            {t('auth.login.identifierLabel')}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t(
                                'auth.login.identifierPlaceholder'
                              )}
                              className="h-11 rounded-xl bg-muted/50 border-border/50 focus-visible:ring-primary/30"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center justify-between">
                            <FormLabel className="text-sm font-medium">
                              {t('auth.login.passwordLabel')}
                            </FormLabel>
                            <Button
                              variant="link"
                              type="button"
                              className="h-auto p-0 text-xs text-primary/80 hover:text-primary"
                              onClick={() =>
                                navigate({ to: '/forgot-password' })
                              }
                            >
                              {t('auth.login.forgotPassword')}
                            </Button>
                          </div>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type={showPassword ? 'text' : 'password'}
                                placeholder={t(
                                  'auth.login.passwordPlaceholder'
                                )}
                                className="h-11 rounded-xl bg-muted/50 border-border/50 focus-visible:ring-primary/30 pr-10"
                                {...field}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="absolute right-0 top-0 h-full px-3 py-2 z-10 hover:bg-transparent"
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={t(
                                  showPassword
                                    ? 'auth.login.hidePassword'
                                    : 'auth.login.showPassword'
                                )}
                              >
                                {showPassword ? (
                                  <EyeOff className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
                                ) : (
                                  <Eye className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
                                )}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="submit"
                      className="w-full h-11 rounded-xl font-semibold text-sm bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all duration-300"
                      disabled={form.formState.isSubmitting}
                    >
                      {form.formState.isSubmitting ? (
                        <>
                          <ButtonSpinner className="mr-2" />
                          {t('common.loading.default')}
                        </>
                      ) : (
                        t('auth.login.submitButton')
                      )}
                    </Button>
                  </form>
                </Form>
              )}
            </div>
          </div>

          {/* Security badge */}
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground/70">
            <Shield className="h-3.5 w-3.5" />
            <span>{t('auth.login.securityBadge')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
